import { useEffect, useMemo, useState } from 'react';
import { Check, UserPlus, Users, X } from 'lucide-react';

import SearchInputField from '../../../../components/Forms/Input/SearchInputField';
import ActionButton from '../../../../components/Buttons/ActionButton';

import { RestaurantPartyOption } from '../../../../types/restaurant/RestaurantOrderTypes';

interface OrderCustomerSelectorProps {
  open: boolean;
  customers: RestaurantPartyOption[];
  currentCustomerId: number | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (customerId: number) => Promise<unknown> | void;
  onAddCustomer: () => void;
}

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';

const OrderCustomerSelector = ({
  open,
  customers,
  currentCustomerId,
  busy,
  onOpenChange,
  onConfirm,
  onAddCustomer,
}: OrderCustomerSelectorProps) => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(currentCustomerId);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedId(currentCustomerId);
    }
  }, [currentCustomerId, open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onOpenChange, open]);

  const filteredCustomers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return customers;
    return customers.filter((customer) =>
      `${customer.code ?? ''} ${customer.name}`.toLowerCase().includes(query)
    );
  }, [customers, search]);

  if (!open) return null;

  const unchanged = selectedId === currentCustomerId;

  return (
    <div
      className="fixed inset-0 z-[8999] flex items-center justify-center bg-black/60 px-3 py-6 backdrop-blur-[2px]"
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-customer-selector-title"
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark"
      >
        <header className="flex items-start justify-between gap-3 border-b border-stroke px-5 py-4 dark:border-strokedark">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-primary/10 p-2 text-primary">
              <Users className="h-5 w-5" />
            </span>
            <div>
              <h2
                id="order-customer-selector-title"
                className="font-semibold text-black dark:text-white"
              >
                Cambiar cliente
              </h2>
              <p className="mt-0.5 text-xs text-bodydark2">
                Elegí a quién se le factura esta orden.
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Cerrar selector de clientes"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-2 text-bodydark2 transition hover:bg-gray-2 dark:hover:bg-meta-4"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="border-b border-stroke p-4 dark:border-strokedark">
          <SearchInputField
            name="customerSearch"
            value={search}
            placeholder="Buscar por código o nombre..."
            withMargin={false}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="flex-1 space-y-1.5 overflow-y-auto p-3">
          {filteredCustomers.map((customer) => {
            const active = selectedId === customer.id;
            return (
              <button
                key={customer.id}
                type="button"
                onClick={() => setSelectedId(customer.id)}
                className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition ${
                  active
                    ? 'border-primary bg-primary/5'
                    : 'border-stroke hover:border-primary/40 hover:bg-primary/5 dark:border-strokedark'
                }`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                    active
                      ? 'bg-primary text-white'
                      : 'bg-gray-2 text-body dark:bg-meta-4 dark:text-bodydark'
                  }`}
                >
                  {initials(customer.name)}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-black dark:text-white">
                      {customer.name}
                    </span>
                    {customer.defaultCustomer && (
                      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                        Predeterminado
                      </span>
                    )}
                  </span>
                  {customer.code && (
                    <span className="block truncate text-xs text-bodydark2">
                      {customer.code}
                    </span>
                  )}
                </span>

                {active && (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                )}
              </button>
            );
          })}

          {!filteredCustomers.length && (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <Users className="h-8 w-8 text-bodydark2" />
              <p className="text-sm text-bodydark2">
                No se encontraron clientes activos.
              </p>
            </div>
          )}
        </div>

        <footer className="space-y-3 border-t border-stroke p-4 dark:border-strokedark">
          <ActionButton
            label="Agregar nuevo cliente"
            icon={<UserPlus className="h-4 w-4" />}
            variant="outline"
            size="md"
            onClick={onAddCustomer}
          />

          <div className="grid grid-cols-2 gap-3">
            <ActionButton
              label="Cancelar"
              variant="secondary"
              size="md"
              onClick={() => onOpenChange(false)}
            />
            <ActionButton
              label="Confirmar"
              variant="primary"
              size="md"
              loading={busy}
              disabled={!selectedId || unchanged || busy}
              onClick={async () => {
                if (!selectedId || unchanged) return;
                await onConfirm(selectedId);
              }}
            />
          </div>
        </footer>
      </section>
    </div>
  );
};

export default OrderCustomerSelector;
